<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@/ui/validation/validationRules";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useBaseStore } from "@/stores/base";

type PaletteScope = "global" | "personal" | "all";

const props = withDefaults(defineProps<{ scope?: PaletteScope }>(), { scope: "all" });
const isVisible = defineModel<boolean>();
const emit = defineEmits<{ created: [] }>();

const { t } = useI18n();

const isFormValid = ref(false);
const isLoading = ref(false);
const name = ref("");
const userId = ref<number | null>(
	props.scope === "personal" ? (useBaseStore().currentUser?.id ?? null) : null
);

const userOptions = [
	{ name: "Global", id: null },
	{ name: "Personal", id: useBaseStore().currentUser?.id },
];

const showOwnerField = computed(() => props.scope === "all");

const api = computed(() =>
	props.scope === "personal" ? useApiClient("profile") : useApiClient("environment")
);

const resetForm = () => {
	name.value = "";
	userId.value = props.scope === "personal" ? (useBaseStore().currentUser?.id ?? null) : null;
	isFormValid.value = false;
};

const createColorPalette = async () => {
	isLoading.value = true;
	try {
		const response = await toastApiResult(
			api.value.createColorPalette({
				name: name.value,
				is_default: false,
				user_id: userId.value,
				colors: [],
			})
		);
		if (response.isOk()) {
			emit("created");
			resetForm();
			isVisible.value = false;
		}
	} finally {
		isLoading.value = false;
	}
};
</script>

<template>
	<v-dialog v-model="isVisible" persistent width="500">
		<v-form v-model="isFormValid" @submit.prevent="createColorPalette">
			<v-card elevation="9">
				<v-toolbar flat>
					<v-toolbar-title>
						<i class="fa-duotone fa-palette fa-lg mr-3" />
						New Color Palette
					</v-toolbar-title>
					<v-spacer />
					<v-btn icon="mdi-close" @click="isVisible = false" />
				</v-toolbar>

				<v-divider />

				<div class="pa-5 d-flex flex-column ga-4">
					<div>
						<h6 class="text-h6 mb-3">Name:</h6>
						<v-text-field
							v-model="name"
							:rules="[required(t)]"
							hide-details="auto"
							variant="filled"
							placeholder="Enter palette name"
							:disabled="isLoading"
							autofocus
						/>
					</div>
					<div v-if="showOwnerField">
						<h6 class="text-h6 mb-3">Owner:</h6>
						<v-select
							v-model="userId"
							:items="userOptions"
							item-title="name"
							item-value="id"
							hide-details="auto"
							variant="filled"
							:disabled="isLoading"
						/>
					</div>
				</div>

				<v-divider />

				<v-card-actions class="mx-2 my-1">
					<v-spacer />
					<v-btn color="error" variant="text" @click="isVisible = false">
						Cancel
					</v-btn>
					<v-btn
						color="secondary"
						variant="flat"
						:disabled="!isFormValid || isLoading"
						:loading="isLoading"
						@click="createColorPalette"
					>
						Create
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>
